import React, { createContext, useContext, useMemo } from "react";
import { usePatientGetDetail } from "../lib/api/generated/sASOnlineClinicBackendAPI";
import { parsePatientDetailOk } from "../utils/patient-detail-parser";

type OwnPatientDetailType = {
  userId?: string;
  patientId?: string;
  patientName?: string;
  companyName?: string;
  statusCpap?: string;
  cpapOnlineReservationAllowed?: boolean;
  cpapOnlineBlockReason?: string;
  isLoading?: boolean;
  errorStatus?: number;
  hasError?: boolean;
};

const OwnPatientDetailContext = createContext<OwnPatientDetailType | undefined>(
  undefined,
);

export function OwnPatientDetailProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const detailQuery = usePatientGetDetail("me");
  const errorStatus = useMemo<number | undefined>(() => {
    if (detailQuery?.isError) {
      const anyErr = detailQuery.error as any;
      const s = Number(anyErr?.status ?? anyErr?.response?.status);
      return Number.isFinite(s) ? s : undefined;
    }
    const resp = detailQuery?.data as any;
    const s = Number(resp?.status);
    if (Number.isFinite(s) && s !== 200) return s;
    return undefined;
  }, [detailQuery?.isError, detailQuery?.error, detailQuery?.data]);

  const hasError = useMemo<boolean>(() => {
    if (detailQuery?.isError) return true;
    const resp = detailQuery?.data as any;
    const s = Number(resp?.status);
    return Number.isFinite(s) && s !== 200;
  }, [detailQuery?.isError, detailQuery?.data]);
  const detail = useMemo(
    () => parsePatientDetailOk(detailQuery?.data),
    [detailQuery?.data],
  );

  const value = useMemo<OwnPatientDetailType>(
    () => ({
      userId: detail?.userId,
      // 表示用の患者ID/氏名/所属名を集約。氏名はnameが唯一の正とし、
      // 所属名は organizationName を優先し、なければ通院先の clinicName を使用。
      patientId: detail?.patientId,
      patientName: detail?.name,
      companyName:
        detail?.organizationName ??
        detail?.currentClinic?.clinicName ??
        undefined,
      statusCpap: detail?.statusCpap,
      cpapOnlineReservationAllowed:
        detail?.cpapOnlineReservationAllowed ?? undefined,
      cpapOnlineBlockReason: detail?.cpapOnlineBlockReason ?? undefined,
      isLoading: Boolean(detailQuery?.isLoading),
      errorStatus: errorStatus,
      hasError: hasError,
    }),
    [detail, detailQuery?.isLoading, errorStatus, hasError],
  );

  return (
    <OwnPatientDetailContext.Provider value={value}>
      {children}
    </OwnPatientDetailContext.Provider>
  );
}

export function useOwnPatientDetail() {
  const context = useContext(OwnPatientDetailContext);
  if (context === undefined) {
    throw new Error(
      "useOwnPatientDetail must be used within an OwnPatientDetailProvider",
    );
  }
  return context;
}
